import { jsx as _jsx } from "react/jsx-runtime";
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { headingVariants } from '@/components/atoms/Typography/Heading';
import { useDataLayer } from '@/hooks/useDataLayer';
import { cn } from '@/utils/cn';
import { forwardRef } from 'react';
const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-base md:text-lg font-bold transition-palette-animate hover:transition-all disabled:pointer-events-none disabled:bg-surface-button-primary-disabled disabled:text-type-button-primary-disabled [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-6 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-offset-2 focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer", {
    variants: {
        variant: {
            default: 'bg-surface-button-primary-default text-type-button-primary hover:bg-surface-button-primary-hover hover:text-type-button-primary-hover',
            secondary: 'border hover:bg-surface-button-secondary-hover hover:text-white hover:border-surface-button-secondary-hover',
            tertiary: 'underline font-normal disabled:bg-inherit text-type-button-tertiary hover:text-type-button-tertiary-hover',
            hero: 'bg-surface-button-hero-default text-type-button-hero hover:bg-surface-button-hero-hover hover:text-type-button-hero-hover',
            nav: `${headingVariants({ variant: 'h3' })} hover:text-type-emphasis`,
            'sub-nav': 'hover:text-type-emphasis',
            ghost: '',
            expand: cn('relative flex h-[1.15rem] w-[1.15rem] items-center justify-center transition-colors duration-300 ease-out', 'before:absolute before:inset-0 before:scale-0 before:rounded-full before:bg-neutral-300 before:text-white before:transition-transform before:duration-300 before:ease-out before:content-[""]', 'hover:before:translate-z-0 hover:before:scale-100 [&_svg]:z-10 hover:[&_svg]:text-white'),
            'menu-option': 'border bg-surface-primary-default text-type-default border-button-disabled rounded-sm font-normal hover:bg-surface-button-primary-hover hover:text-type-button-primary-hover hover:border-button-hover',
            chip: 'rounded-sm font-normal border text-xs',
            chipSmall: 'rounded-sm font-normal border text-xs md:text-xs',
            utility: "[&_svg:not([class*='size-'])]:size-5 rounded-sm font-normal border text-xs  md:text-xs  border-stroke-buttons-utility hover:bg-surface-button-utility-hover active:bg-surface-button-utility-hover",
        },
        size: {
            default: 'px-6 py-2 md:px-6 md:py-3 tracking-[-0.16px] md:tracking-[-0.18px]',
            small: 'px-6 py-2 md:px-5 text-xs tracking-[-0.16px] md:tracking-[-0.12px]',
            'small-text': 'text-xs md:text-xs',
            icon: 'size-10 md:size-12',
            'icon-small': 'size-10 md:size-9 ',
            menu: 'py-6 px-0 w-full justify-start hover:px-2 rounded-sm ',
            'sub-menu': 'py-2 px-0 w-full justify-start rounded-none',
            chip: 'py-1 px-2',
            chipSmall: 'py-1 px-2',
            ghost: '',
            utility: 'px-3 md:px-4 py-2',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'default',
    },
});
const Button = forwardRef(function ({ className, variant, size, asChild = false, onClick, noStyle, ...props }, ref) {
    const Comp = asChild ? Slot : 'button';
    const dataLayer = useDataLayer();
    return (_jsx(Comp, { "data-slot": "button", ref: ref, className: noStyle ? className : cn(buttonVariants({ variant, size, className })), onClick: (p) => {
            if (onClick)
                onClick(p);
            if (dataLayer.contextParameters.event)
                dataLayer.push({
                    event: dataLayer.contextParameters.event,
                });
        }, ...props }));
});
export { Button, buttonVariants };
